
import {Badge, Button, Card, Container, Group, SimpleGrid, Stack, Table, Text, Title, useMantineColorScheme, Box} from "@mantine/core";
import {useNavigate, useParams} from "react-router-dom";
import MatchHeaderCard from "./MatchHeaderCard";
import MapIcon from "components/Graphics/MapIcon";

/**
 * GameDetails
 *
 * Renders details about a single game (map) within a match. Shows a header indicating
 * which game is open and replaces the match-level scoreboard with game-specific stats.
 * Route: /matches/:matchId/games/:gameNo
 */
export default function GameDetails() {
  const { matchId, gameNo } = useParams<{ matchId: string; gameNo: string }>();
  const navigate = useNavigate();
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === 'dark';

  // Mock data — keep in sync with MatchDetails mock until wired to API
  const match = getMockMatchById(matchId ?? 'm1');
  const bestOf = match.maps?.length ?? undefined;

  const n = Math.max(1, Math.min(Number(gameNo ?? '1') || 1, match.maps?.length ?? 1));
  const idx = n - 1;
  const map = match.maps?.[idx];

  // Derive per-game player stats deterministically from base match scoreboard
  const homeRows = deriveGameScoreboard(match.homeScoreboard, idx);
  const awayRows = deriveGameScoreboard(match.awayScoreboard, idx);

  return (
    <Container size="lg" p="md">
      <Stack gap="md">
        <Group justify="space-between" align="center">
          <Title order={3} c={isDark ? 'gray.1' : 'dark.5'}>Game Details</Title>
          <Group gap="xs">
            <Button variant="light" onClick={() => navigate(`/matches/${match.id}`)}>Back to Match</Button>
          </Group>
        </Group>

        {/* Overall match header for context (with BO-X) */}
        <MatchHeaderCard match={{ ...match, bestOf }} />

        {/* Game header with central map icon and side scores */}
        <Card withBorder radius="md" p="sm">
          <Box style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center' }}>
            {/* Left: Home score badge */}
            <Box style={{ justifySelf: 'end' }}>
              {(() => {
                const parsed = parseMapScore(map?.score);
                const home = parsed?.home; const away = parsed?.away;
                const isDraw = typeof home === 'number' && typeof away === 'number' && home === away;
                const homeWon = typeof home === 'number' && typeof away === 'number' && home > away;
                const badgeVariant: 'filled' | 'light' = isDark ? 'filled' : 'light';
                return (
                  <Badge variant={badgeVariant} color={isDraw ? 'gray' : (homeWon ? 'green' : 'red')}>
                    {typeof home === 'number' ? home : '-'}
                  </Badge>
                );
              })()}
            </Box>

            {/* Center: Map number, icon, and name */}
            <Stack gap={4} align="center" justify="center">
              <Badge variant={isDark ? 'filled' : 'light'} className="text-gray-900" color={isDark ? 'gray' : 'dark'} size="xs">Map {n}</Badge>
              <MapIcon name={map?.mapId as any} size={32} />
              {map && (
                <Text ta="center" fw={600} c={isDark ? 'gray.0' : 'dark.7'}>{map.name}</Text>
              )}
            </Stack>

            {/* Right: Away score badge */}
            <Box style={{ justifySelf: 'start' }}>
              {(() => {
                const parsed = parseMapScore(map?.score);
                const home = parsed?.home; const away = parsed?.away;
                const isDraw = typeof home === 'number' && typeof away === 'number' && home === away;
                const awayWon = typeof home === 'number' && typeof away === 'number' && away > home;
                const badgeVariant: 'filled' | 'light' = isDark ? 'filled' : 'light';
                return (
                  <Badge variant={badgeVariant} color={isDraw ? 'gray' : (awayWon ? 'green' : 'red')}>
                    {typeof away === 'number' ? away : '-'}
                  </Badge>
                );
              })()}
            </Box>
          </Box>
        </Card>

        {/* Per-game scoreboards (replace match-level scoreboard) */}
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
          <ScoreboardCard title={`${match.homeTeam} Players`} rows={homeRows} />
          <ScoreboardCard title={`${match.awayTeam} Players`} rows={awayRows} />
        </SimpleGrid>

        {/* Secondary return control at bottom for convenience */}
        <Group justify="center">
          <Button onClick={() => navigate(`/matches/${match.id}`)} variant="subtle">Return to Match Details</Button>
        </Group>
      </Stack>
    </Container>
  );
}

// --- Local helpers (mock) ---

function deriveGameScoreboard(rows: ReadonlyArray<{ player: string; kills: number; deaths: number; adr?: number }>, seed: number) {
  // Create deterministic per-map variance using seed
  const delta = (i: number) => ((i + 1) * (seed + 1)) % 5; // 0..4
  return rows.map((r, i) => ({
    player: r.player,
    // Slightly vary kills/deaths, clamp to sensible ranges
    kills: Math.max(0, r.kills + delta(i) - 2),
    deaths: Math.max(0, r.deaths - delta(i) + 2),
    adr: typeof r.adr === 'number' ? Math.max(40, Math.round(r.adr + (delta(i) - 2) * 4)) : undefined,
  }));
}

function parseMapScore(score?: string): { home: number; away: number } | undefined {
  if (!score) return undefined;
  const m = score.match(/^\s*(\d+)\s*[-:]\s*(\d+)\s*$/);
  if (!m) return undefined;
  return { home: parseInt(m[1], 10), away: parseInt(m[2], 10) };
}

function ScoreboardCard({ title, rows }: { title: string; rows: ReadonlyArray<{ player: string; kills: number; deaths: number; adr?: number }> }) {
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === 'dark';
  return (
    <Card withBorder radius="md" p="md">
      <Stack gap="sm">
        <Text fw={700} size="sm" c={isDark ? 'gray.0' : 'dark.7'}>{title}</Text>
        <Table striped withTableBorder horizontalSpacing="md" verticalSpacing="xs">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Player</Table.Th>
              <Table.Th>K</Table.Th>
              <Table.Th>D</Table.Th>
              <Table.Th>ADR</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map((r) => (
              <Table.Tr key={r.player}>
                <Table.Td>{r.player}</Table.Td>
                <Table.Td>{r.kills}</Table.Td>
                <Table.Td>{r.deaths}</Table.Td>
                <Table.Td>{typeof r.adr === 'number' ? Math.round(r.adr) : '-'}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Stack>
    </Card>
  );
}

function getMockMatchById(id: string) {
  const base = {
    scheduledAt: new Date(Date.now() - 1000 * 60 * 60).toISOString(),
    tournament: 'CSC League Week 5',
    maps: [
      { name: 'Mirage', mapId: 'de_mirage', score: '16-12' },
      { name: 'Inferno', mapId: 'de_inferno', score: '12-16' },
      { name: 'Anubis', mapId: 'de_anubis', score: '16-10' },
    ],
    homeScoreboard: [
      { player: 'Player One', kills: 24, deaths: 18, adr: 92.3 },
      { player: 'Player Two', kills: 20, deaths: 17, adr: 86.1 },
      { player: 'Player Three', kills: 18, deaths: 20, adr: 78.5 },
      { player: 'Player Four', kills: 16, deaths: 19, adr: 72.9 },
      { player: 'Player Five', kills: 14, deaths: 21, adr: 68.2 },
    ],
    awayScoreboard: [
      { player: 'Opp One', kills: 22, deaths: 22, adr: 88.2 },
      { player: 'Opp Two', kills: 19, deaths: 21, adr: 79.4 },
      { player: 'Opp Three', kills: 17, deaths: 20, adr: 75.0 },
      { player: 'Opp Four', kills: 15, deaths: 23, adr: 70.1 },
      { player: 'Opp Five', kills: 13, deaths: 22, adr: 64.7 },
    ],
  } as const;

  const data = {
    m1: { id: 'm1', status: 'completed' as const, result: 'W' as const, score: '2-1', homeTeam: 'Tigers', awayTeam: 'Phoenix', ...base },
    m2: { id: 'm2', status: 'completed' as const, result: 'L' as const, score: '0-2', homeTeam: 'Tigers', awayTeam: 'North Wolves', ...base },
    pm1: { id: 'pm1', status: 'scheduled' as const, result: undefined, score: undefined, homeTeam: 'Tigers', awayTeam: 'North Wolves', ...base },
    ip1: { id: 'ip1', status: 'live' as const, inProgress: true as const, result: undefined, score: '1-0', homeTeam: 'Tigers', awayTeam: 'Phoenix', ...base },
    fm1: { id: 'fm1', status: 'scheduled' as const, result: undefined, score: undefined, homeTeam: 'Moist Tigers', awayTeam: 'Northern Wolves Org', ...base },
    fm2: { id: 'fm2', status: 'completed' as const, result: 'L' as const, score: '1-2', homeTeam: 'Moist Tigers', awayTeam: 'Phoenix Org', ...base },
    fmd1: { id: 'fmd1', status: 'completed' as const, result: 'L' as const, score: '2-2', homeTeam: 'Moist Tigers', awayTeam: 'Phoenix Org', ...base },
  };

  return data[id as keyof typeof data] ?? { id, status: 'scheduled' as const, result: undefined, score: undefined, homeTeam: 'Home', awayTeam: 'Away', ...base };
}
